// Cost waterfall — horizontal recharts waterfall (stacked bars with an invisible
// offset series) plus the full step table, which doubles as the accessible
// alternative to the chart.
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TD, TD_NUM, TH } from "../../../../components/ui";
import { fmtCurrency, fmtCurrencyShort } from "../../../../utils/format";

const FILL = {
  add: "var(--color-primary)",
  subtotal: "var(--color-text-muted)",
  total: "var(--color-secondary)",
  muted: "var(--color-border-strong)",
};

/** Steps → [{ label, offset, value, kind }] where offset is the invisible lead-in. */
export const toWaterfallData = (steps) => {
  let running = 0;
  return steps.map((s) => {
    if (s.type === "add") {
      const row = { label: s.label, offset: running, value: s.value, kind: s.muted ? "muted" : "add" };
      running += s.value;
      return row;
    }
    running = s.value;
    return { label: s.label, offset: 0, value: s.value, kind: s.type };
  });
};

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-[4px] border border-border bg-surface px-2.5 py-1.5 text-[12px] shadow-sm">
      <div className="font-semibold text-text_primary">{d.label}</div>
      <div className="tabular-nums text-text_secondary">{fmtCurrency(d.value)}</div>
    </div>
  );
}

export function WaterfallChart({ steps }) {
  const data = toWaterfallData(steps);
  const total = steps.find((s) => s.type === "total")?.value || 0;
  return (
    <div
      className="h-[440px] w-full print:h-[380px]"
      role="img"
      aria-label={`Cost waterfall from material to a total bid price of ${fmtCurrency(total)}. Full figures are in the table below.`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, bottom: 4, left: 8 }} barCategoryGap={4}>
          <CartesianGrid horizontal={false} stroke="var(--color-border-soft)" />
          <XAxis
            type="number"
            tickFormatter={fmtCurrencyShort}
            tick={{ fill: "var(--color-text-muted)", fontSize: 11 }}
            axisLine={{ stroke: "var(--color-border)" }}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            width={210}
            tick={{ fill: "var(--color-text-secondary)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<ChartTooltip />} cursor={{ fill: "var(--color-surface-alt)" }} />
          <Bar dataKey="offset" stackId="w" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="value" stackId="w" radius={[0, 2, 2, 0]} isAnimationActive={false}>
            {data.map((d) => (
              <Cell key={d.label} fill={FILL[d.kind]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function WaterfallTable({ steps }) {
  const total = steps.find((s) => s.type === "total")?.value || 0;
  return (
    <table className="w-full border-collapse" aria-label="Cost waterfall">
      <thead>
        <tr>
          <th className={TH}>Step</th>
          <th className={`${TH} text-right`}>Amount</th>
          <th className={`${TH} text-right`}>% of bid</th>
        </tr>
      </thead>
      <tbody>
        {steps.map((s) => {
          const strong = s.type !== "add";
          const muted = s.muted ? "text-text_muted" : "";
          const rowBg = s.type === "total" ? "bg-secondary_tint" : s.type === "subtotal" ? "bg-surface_alt" : "";
          return (
            <tr key={s.key} className={rowBg}>
              <td className={`${TD} ${strong ? "font-semibold" : "pl-6"} ${muted} ${s.type === "total" ? "text-secondary" : ""}`}>
                {s.label}
                {s.muted && <span className="ml-1.5 text-[11px] font-normal">(not required)</span>}
              </td>
              <td className={`${TD_NUM} ${strong ? "font-semibold" : ""} ${muted} ${s.type === "total" ? "text-secondary" : ""}`}>{fmtCurrency(s.value)}</td>
              <td className={`${TD_NUM} text-text_muted`}>{total > 0 ? `${((s.value / total) * 100).toFixed(1)}%` : "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
